import { useGateValue } from '@statsig/expo-bindings'
import { router } from 'expo-router'
import { useLayoutEffect, useState } from 'react'
import { InteractionManager, Platform } from 'react-native'

import { useTour } from '@/components/tour/TourProvider'
import type { TourConfig, TourStep } from '@/components/tour/types'
import { AppTourState } from '@/store/appTourSlice'
import { useAppStore } from '@/store/useAppStore'
import { AppTour, AppTourKeys, Feature } from '../../constants'

type AppTourKey = (typeof AppTourKeys)[keyof typeof AppTourKeys]

interface Props {
  enabled?: boolean
  tourKey: AppTourKey
}

function buildTourConfig(
  tourKey: AppTourKey,
  onStop: (completed: boolean) => void
): TourConfig | null {
  const tourSteps = AppTour[tourKey as keyof typeof AppTour]
  if (!tourSteps) return null

  const steps: TourStep[] = Object.values(tourSteps)
    .sort((a, b) => a.order - b.order)
    .map((step) => ({
      spotId: step.name,
      text: step.text,
      navigateTo: step.page || undefined,
    }))

  return {
    id: tourKey,
    steps,
    onStop,
    onBeforeStepChange: async (fromIndex, toIndex) => {
      const fromStep = steps[fromIndex]
      const toStep = steps[toIndex]

      // Dismiss bottom sheets when navigating away from filter screens
      if (
        fromStep?.navigateTo?.startsWith('/fosters/filters') &&
        !toStep?.navigateTo?.startsWith('/fosters/filters')
      ) {
        router.dismiss()
      }
    },
  }
}

const useAppTour = ({ enabled = true, tourKey }: Props) => {
  const [isLayoutLoaded, setIsLayoutLoaded] = useState(false)

  const { start, isActive } = useTour()
  const appTours = useAppStore.use.appTour()
  const setAppTourStepComplete = useAppStore.use.setAppTourStepComplete()
  const isAppTourStepComplete = appTours[tourKey as keyof AppTourState]

  const hasFirstRunExperience = useGateValue(Feature.FirstRunExperience)

  useLayoutEffect(() => {
    if (
      Platform.OS === 'web' ||
      !hasFirstRunExperience ||
      isAppTourStepComplete ||
      !enabled ||
      isActive
    ) {
      return
    }

    if (!isLayoutLoaded) {
      InteractionManager.runAfterInteractions(() => {
        setIsLayoutLoaded(true)
      })
    }

    if (!isLayoutLoaded) {
      return
    }

    const config = buildTourConfig(tourKey, () => {
      setAppTourStepComplete(tourKey, true)
    })

    if (config) {
      start(config)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    isAppTourStepComplete,
    hasFirstRunExperience,
    isLayoutLoaded,
    isActive,
  ])
}

export default useAppTour
